import {
  ShiftManagementAreasLocationsEmployeesDocument,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementMainPageDocument,
  ShiftManagementSelectableShiftsRowsDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { LOGIC_OPERATOR, SHIFT_CONFLICTS } from 'modules/shift_management/hooks/reducers/filter'
import {
  MAX_ITEMS_LIMIT_NO_PAGINATION,
  MAX_ITEMS_PER_PAGE,
  MAX_WORKAREAS_PER_PAGE,
} from 'modules/shift_management/hooks/reducers/pagination'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useOrderOptions } from 'modules/shift_management/hooks/useOrderEmployees'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import ShiftsRow, {
  ShiftsRowType,
  TShiftsRow,
  TWorkAreaShiftsRow,
} from 'modules/shift_management/store/internal/models/ShiftsRow'

interface UseGetShiftsRowsProps {
  printSchedule?: boolean
}
const useGetShiftsRows = ({ printSchedule = false }: UseGetShiftsRowsProps = {}): {
  shiftsRows: ShiftsRow[]
  loading: boolean
} => {
  const [filters] = useFilters()
  const [{ page }] = usePagination()
  const [{ view }] = useSchedule()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { startAt, endAt } = view
  const hasLegacyJobCatalogFeature = hasFeatureFlag('legacy_job_catalog')
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()
  const currentPage = filters.statusId === SHIFT_CONFLICTS ? null : page
  const selectableEmployees = useSelectableEmployees(
    view.startAt,
    view.endAt,
    'read',
    filters.locationIds
  )
  const { loading: selectableEmployeesLoading, selectableEmployeeIds } = selectableEmployees

  const filteredEmployeeIds = useUnsafeMemo(() => {
    if (filters.terminated) {
      const requestedIds = filters.employeeIds
      return requestedIds && requestedIds.length > 0 ? requestedIds : null
    }

    const requestedIds = filters.employeeIds
    // If there are not filters (requestedIds is null), then show all the selectable employee ids.
    if (!requestedIds) return null

    // If there are requested ids, filter them to only include the selectable employee ids.
    return requestedIds.filter((id) => selectableEmployeeIds.includes(id))
  }, [filters.employeeIds, filters.terminated, selectableEmployeeIds])

  const variables = {
    contractOnDate: startAt,
    orderBy: localStorageOrderEmployeesBy,
    orderDir: localStorageOrderEmployeesDir,
    page: printSchedule ? 1 : currentPage,
    perPage: printSchedule ? MAX_ITEMS_LIMIT_NO_PAGINATION : MAX_ITEMS_PER_PAGE,
    customFilters: filters.customFilters,
    // - Accept employeeIds to refresh some isolated rows on update/delete
    employeeIds: filteredEmployeeIds,
    endAt,
    endAtISO: endAt,
    logicOperator: LOGIC_OPERATOR.AND,
    shiftIds: filters.shiftIds,
    withoutShiftIds: filters.withoutShiftIds,
    startAt,
    startAtISO: startAt,
    terminated: filters.terminated,
    locationIds: filters.locationIds,
    teamIds: filters.teamIds,
    legalEntityIds: filters.legalEntityIds,
  }

  const { page: _page, perPage: _perPage, ...variablesWithoutPagination } = variables

  const { loading: employeesLoading, data: employeesData } = useQuery(
    ShiftManagementMainPageDocument,
    {
      variables: {
        ...variables,
        perPage: MAX_ITEMS_PER_PAGE,
      },
      skip: !view.isByEmployees || printSchedule,
    }
  )

  const { loading: employeesPrintLoading, data: employeesPrintData } = useQuery(
    ShiftManagementSelectableShiftsRowsDocument,
    {
      variables: {
        ...variables,
      },
      skip: !view.isByEmployees || !printSchedule,
      fetchPolicy: 'network-only',
    }
  )

  const { loading: workAreasLoading, data: workAreasData } = useQuery(
    ShiftManagementAreasViewPageDocument,
    {
      variables: {
        ...variables,
        perPage: MAX_WORKAREAS_PER_PAGE,
      },
      skip: !view.isByAreas,
      fetchPolicy: 'network-only',
    }
  )

  const { loading: locationsEmployeesLoading, data: locationsEmployeesData } = useQuery(
    ShiftManagementAreasLocationsEmployeesDocument,
    {
      variables: variablesWithoutPagination,
      skip: !view.isByAreas,
      fetchPolicy: 'network-only',
    }
  )

  const shiftsRows = useUnsafeMemo(() => {
    if (printSchedule) {
      if (!view.isByEmployees) return []

      const result = employeesPrintData?.shiftManagement?.employeesPermissions || []
      return result.map((shiftsRow: TShiftsRow) =>
        ShiftsRow.buildFromGraphQL(shiftsRow, ShiftsRowType.employee, hasLegacyJobCatalogFeature)
      )
    }
    if (view.isByEmployees) {
      const result = employeesData?.shiftManagement?.employeesPermissionsConnection?.nodes || []

      return result.map((shiftsRow: TShiftsRow) =>
        ShiftsRow.buildFromGraphQL(shiftsRow, ShiftsRowType.employee, hasLegacyJobCatalogFeature)
      )
    } else {
      const data = workAreasData?.shiftManagement?.workAreasConnection?.nodes || []
      const locations = locationsEmployeesData?.locations?.locations || []
      const employees = locationsEmployeesData?.shiftManagement?.employeesPermissions || []
      return compact(
        data.map((shiftsRow: TWorkAreaShiftsRow) => {
          const location = locations.find((l) => l.id === shiftsRow.location?.id)
          if (!location) return
          return ShiftsRow.buildFromWorkArea(shiftsRow, location, employees)
        })
      )
    }
  }, [
    view.isByEmployees,
    employeesPrintData?.shiftManagement?.employeesPermissions,
    employeesData?.shiftManagement?.employeesPermissionsConnection?.nodes,
    workAreasData?.shiftManagement?.workAreasConnection?.nodes,
    locationsEmployeesData?.locations?.locations,
    locationsEmployeesData?.shiftManagement?.employeesPermissions,
    printSchedule,
    hasLegacyJobCatalogFeature,
  ])

  if (view.isByAreas)
    return {
      shiftsRows,
      loading: selectableEmployeesLoading || workAreasLoading || locationsEmployeesLoading,
    }

  return {
    shiftsRows,
    loading: selectableEmployeesLoading || employeesLoading || employeesPrintLoading,
  }
}

export default useGetShiftsRows
